import { useEffect, useMemo, useRef } from 'react';
import {debounce, DebouncedFunc} from 'lodash';

type AnyFn = (...args: any[]) => any;

export function useDebouncedCallback<T extends AnyFn>(
  fn: T,
  wait: number,
  options?: Parameters<typeof debounce>[2]
): DebouncedFunc<T> {
  const fnRef = useRef(fn);
  const optionsRef = useRef(options);

  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const debounced = useMemo(() => {
    const wrapped = ((...args: any[]) => fnRef.current(...args)) as T;
    return debounce(wrapped, wait, optionsRef.current) as DebouncedFunc<T>;
  }, [wait]);

  useEffect(() => {
    return () => {
      debounced.cancel();
    };
  }, [debounced]);

  return debounced;
}